import { defaultLocale, isSupportedLocale, localeCookieName, type Locale } from './config';
import { resolveLocaleFromAcceptLanguage } from './path';

const localeStorageKey = 'admin-preferred-locale';
const cookieMaxAgeSeconds = 60 * 60 * 24 * 365;

function readCookieValue(cookieName: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const targetPrefix = `${cookieName}=`;
  const cookies = document.cookie.split(';');

  for (const rawCookie of cookies) {
    const cookie = rawCookie.trim();
    if (!cookie.startsWith(targetPrefix)) {
      continue;
    }

    const value = cookie.slice(targetPrefix.length).trim();
    if (!value) {
      return null;
    }

    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  return null;
}

function readStoredLocale(): Locale | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = window.localStorage.getItem(localeStorageKey);
    return isSupportedLocale(value) ? value : null;
  } catch {
    return null;
  }
}

function readCookieLocale(): Locale | null {
  const value = readCookieValue(localeCookieName);
  return isSupportedLocale(value) ? value : null;
}

function readNavigatorLocale(): Locale {
  if (typeof navigator === 'undefined') {
    return defaultLocale;
  }

  return resolveLocaleFromAcceptLanguage(navigator.language);
}

export function resolveClientLocale(): Locale {
  return readCookieLocale() ?? readStoredLocale() ?? readNavigatorLocale();
}

export function persistPreferredLocale(locale: Locale): void {
  if (typeof document !== 'undefined') {
    document.cookie = `${localeCookieName}=${encodeURIComponent(locale)}; path=/; max-age=${cookieMaxAgeSeconds}; SameSite=Lax`;
  }

  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(localeStorageKey, locale);
  } catch {
    // Ignore blocked storage modes.
  }
}
